import { UeElement } from '@oneaudi/falcon-tools';
import { handleOpenInLayer } from '@oneaudi/feature-app-utils';
import {
  SEM_COLOR_ACTION_PRIMARY_SURFACE_STROKE_DEFAULT_LIGHT,
  SYS_BREAKPOINT_MD,
  ThemeProvider,
} from '@oneaudi/unified-web-common';
import { ButtonGroup, TextButton } from '@oneaudi/unified-web-components';
import React, { useContext } from 'react';
import styled from 'styled-components';
import type { CtaButton } from '../../../types';
import { Theme as AppTheme, ContentPosition, CTAsTheme, Mode } from '../../../types';
import { ContentContext } from '../../ContentContext';
import type { Tracking } from '../../hooks/useTracking';
import { ClickableElement } from './RichText';
import { mdWidth } from '../../utils/theme';

export const ButtonsContainer = styled.div<{ displayOnMobile: boolean }>`
  @media (max-width: ${mdWidth}px) {
    display: ${(props) => (props.displayOnMobile ? 'block' : 'none')};
  }
  @media (min-width: ${mdWidth + 1}px) {
    display: ${(props) => (props.displayOnMobile ? 'none' : 'block')};
  }
`;
export const ResponsiveButtonsWrapper = styled.div<{
  isAnchorCTA: boolean;
  mode: Mode;
  contentPosition: ContentPosition;
  isMobile?: boolean;
  backgroundDisclaimersCTAs: CTAsTheme;
  isBottomTextBlock?: boolean;
}>`
  ${({ isAnchorCTA }: { isAnchorCTA: boolean }) =>
    isAnchorCTA &&
    `
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
  `}
  padding: var(--spacing-relative-lg) var(--page-margin) var(--spacing-relative-2xl) var(--page-margin);
  z-index: 4;
  ${({
    mode,
    contentPosition,
    isMobile,
    backgroundDisclaimersCTAs,
    isBottomTextBlock = false,
  }: {
    mode: Mode;
    contentPosition: ContentPosition;
    isMobile?: boolean;
    backgroundDisclaimersCTAs?: CTAsTheme;
    isBottomTextBlock?: boolean;
  }) => {
    const isMobileAndSmall = isMobile && mode === Mode.SMALL;
    const isMobileAndLarge = isMobile && mode === Mode.LARGE;
    const bottomPaddingWithDisclaimers =
      isBottomTextBlock || isMobileAndSmall ? 0 : 'var(--spacing-relative-2xl)';
    const paddingTop = 0;
    const paddingBottom = isMobileAndLarge
      ? `var(--spacing-relative-2xl)`
      : bottomPaddingWithDisclaimers;

    const backgroundColor = (() => {
      switch (backgroundDisclaimersCTAs) {
        case CTAsTheme.Light:
          return SEM_COLOR_ACTION_PRIMARY_SURFACE_STROKE_DEFAULT_LIGHT;
        case CTAsTheme.Dark:
          return SEM_COLOR_ACTION_PRIMARY_SURFACE_STROKE_DEFAULT_LIGHT;
        case CTAsTheme.Media:
          return mode === Mode.SMALL && SEM_COLOR_ACTION_PRIMARY_SURFACE_STROKE_DEFAULT_LIGHT;
        default:
          return SEM_COLOR_ACTION_PRIMARY_SURFACE_STROKE_DEFAULT_LIGHT;
      }
    })();

    if (contentPosition === ContentPosition.TOP_LEFT) {
      return `padding: ${paddingTop} var(--page-margin) ${paddingBottom} var(--page-margin);`;
    }
    if (contentPosition === ContentPosition.BOTTOM_LEFT) {
      return `padding: ${paddingTop} var(--page-margin) ${paddingBottom} var(--page-margin);`;
    }
    if (contentPosition === ContentPosition.CENTER) {
      return `
        display: flex;
        justify-content: center;
        align-items: center;
        padding-top: ${paddingTop};
        padding-bottom: ${paddingBottom};
        background-color: ${isMobile && mode === Mode.SMALL && backgroundColor};
        `;
    }
    if (contentPosition === ContentPosition.TOP_RIGHT) {
      return `padding-bottom: ${paddingBottom};
      display: flex;
      justify-content: end;
      `;
    }
    if (
      backgroundDisclaimersCTAs === CTAsTheme.Light ||
      backgroundDisclaimersCTAs === CTAsTheme.Media
    ) {
      return `backgroundColor: ${isMobile && mode === Mode.SMALL && backgroundColor};`;
    }
    return '';
  }}

  // This hack modifies the button layout between 440px and 767px to stack horizontally, and stretch to full width up until the m breakpoint.
  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    a,
    button {
      width: auto !important;
    }
    position: static;
    left: auto;
    bottom: auto;
    right: auto;
  }
`;

interface StageButtonsProps {
  buttons?: CtaButton[];
  tracking: Tracking;
  isMobile?: boolean;
  isVideoStage?: boolean;
  displayOnMobile: boolean;
}

const StageButtons: React.FC<StageButtonsProps> = ({
  buttons,
  tracking,
  isMobile = false,
  isVideoStage = false,
  displayOnMobile,
}) => {
  const { content, isHeadless } = useContext(ContentContext);

  const { appearance, consumptionAndEmissions, disclaimers, prices, appearancemodelPage } = content;

  // Checks if buttons have been added via the cms
  const hasHeadfullContent = buttons && buttons.length > 0;
  const hasHeadlessContent = buttons && buttons.some((b) => b.label);
  const showPrices = isHeadless && (prices?.totalPrice || prices?.monthlyRate);
  const isModelPage = isHeadless && (showPrices || appearancemodelPage);

  const {
    mode = isModelPage ? Mode.LARGE : Mode.SMALL,
    contentPosition,
    backgroundDisclaimersCTAs,
  } = appearance;
  const isLarge = !isModelPage && mode === Mode.LARGE;
  const isBottomTextBlock =
    consumptionAndEmissions &&
    consumptionAndEmissions.length > 0 &&
    disclaimers &&
    disclaimers.length > 0;

  const isAnchorCTA =
    isLarge &&
    !isVideoStage &&
    (contentPosition === ContentPosition.TOP_LEFT || contentPosition === ContentPosition.TOP_RIGHT);

  if (!hasHeadfullContent || !hasHeadlessContent) return null;
  const Buttons = () => {
    return buttons
      ?.slice(0, 2)
      .map(({ label, url, ariaLabel, openInTabOrLayer, chatBotId, variant = 'primary' }, index) => {
        if (label && openInTabOrLayer === 'chatbot') {
          return (
            <TextButton
              key={`${label}-${String(index)}-chatbot-cta`}
              variant={index === 0 ? variant : 'secondary'}
              aria-label={ariaLabel}
              size={process.env.IS_LEGACY ? 'md' : 'sm'}
              onClick={() => {
                tracking.sendClickEvent({ url, label, index, openInTabOrLayer });
                window.acquireIO.max('text', chatBotId, null, false, 'bot');
              }}
            >
              <UeElement type="text" property={`${getButton(index)}Label`} label="Label">
                {label}
              </UeElement>
            </TextButton>
          );
        }

        if (label && url) {
          const openInLayer = openInTabOrLayer === 'layer';
          const openInTab = openInTabOrLayer === 'tab';
          return (
            <TextButton
              data-testid="button-component"
              key={`${label}-${String(index)}-cta`}
              variant={index === 0 ? variant : 'secondary'}
              aria-label={ariaLabel}
              size={process.env.IS_LEGACY ? 'md' : 'sm'}
              href={url}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                const textBtnUrl = e.currentTarget?.href;
                if (openInLayer) {
                  e.preventDefault();
                  handleOpenInLayer(url, false, true);
                }
                if (openInTab && textBtnUrl) {
                  e.preventDefault();
                  window.open(textBtnUrl, '_blank', 'noopener,noreferrer');
                }
                tracking.sendClickEvent({ url, label, index, openInTabOrLayer });
              }}
            >
              <UeElement type="text" property={`${getButton(index)}Label`} label="Label">
                {label}
              </UeElement>
            </TextButton>
          );
        }

        return null;
      });
  };

  const buttonsTheme = () => {
    if (!process.env.IS_LEGACY) {
      return 'dark';
    }
    if (backgroundDisclaimersCTAs !== CTAsTheme.Media) {
      if (isMobile && mode === Mode.SMALL && !isModelPage) {
        return backgroundDisclaimersCTAs === CTAsTheme.Light ? 'light' : 'dark';
      }
      if (isMobile && mode === Mode.LARGE && !isVideoStage) {
        return appearance.theme === AppTheme.Light ? 'light' : 'dark';
      }
      if (isMobile && isVideoStage && isModelPage) {
        return appearance.theme === AppTheme.Light ? 'light' : 'dark';
      }
      if (!isMobile) {
        return appearance.theme === AppTheme.Light ? 'light' : 'dark';
      }
      return backgroundDisclaimersCTAs === CTAsTheme.Light ? 'light' : 'dark';
    }
    return appearance.theme === AppTheme.Light ? 'light' : 'dark';
  };

  return (
    <ButtonsContainer displayOnMobile={displayOnMobile}>
      <ThemeProvider theme={{ name: buttonsTheme() }}>
        <ResponsiveButtonsWrapper
          isAnchorCTA={isAnchorCTA}
          contentPosition={isModelPage ? ContentPosition.BOTTOM_LEFT : contentPosition}
          mode={isModelPage ? Mode.LARGE : mode}
          isMobile={isMobile}
          backgroundDisclaimersCTAs={isModelPage ? CTAsTheme.Media : backgroundDisclaimersCTAs}
          isBottomTextBlock={isBottomTextBlock}
        >
          <ClickableElement>
            {/* @ts-expect-error Buttons does produce multiple children */}
            <ButtonGroup
              mode="fill"
              margin={['var(--spacing-relative-lg)', 0, 'var(--spacing-relative-2xs)', 0]}
              data-testid={`${isMobile ? 'mobile' : 'desktop'}-responsive-button-group`}
            >
              {Buttons()}
            </ButtonGroup>
          </ClickableElement>
        </ResponsiveButtonsWrapper>
      </ThemeProvider>
    </ButtonsContainer>
  );
};

export default StageButtons;

function getButton(index: number): string {
  if (index === 0) {
    return 'firstButton';
  }
  if (index === 1) {
    return 'secondButton';
  }
  throw new Error('Invalid index. Valid indices are 0 and 1');
}
